import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useChatStore } from "@/stores/chatStore";
import { useNavStore } from "@/stores/navStore";
import { useVisibleTabs } from "@/components/artifacts/RightPanel";
import { apiFetch } from "@/api/client";
import { NATIVE_DOWNLOADS_URL } from "@/pwa";
import { UsageBadge } from "./UsageBadge";

interface UpdateInfo {
  update_available: boolean;
  latest_version?: string;
  download_url?: string;
  sha256?: string;
  changelog?: string;
  can_auto_update?: boolean;
}

type UpdatePhase = "idle" | "downloading" | "ready" | "applying" | "error";

/**
 * True when dotted-numeric version `a` is strictly newer than `b`.
 * Used to detect that a release shipped after we staged a build, so we
 * can discard the stale download and fetch the newer one. Mirrors the
 * backend's `_parse_version` tuple comparison.
 */
function isNewerVersion(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const pa = a.split(".").map((n) => parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x !== y) return x > y;
  }
  return false;
}

export function Header() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  // Mobile-only chrome: the hamburger toggles the off-canvas sidebar drawer,
  // and (on a chat with panel content) a button opens the right panel — which
  // is a full-screen overlay on mobile since its edge strip is desktop-only.
  const toggleMobileNav = useNavStore((s) => s.toggleMobileNav);
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const openRightPanel = useChatStore((s) => s.openRightPanel);
  const { tabs } = useVisibleTabs();
  const [menuOpen, setMenuOpen] = useState(false);
  const [update, setUpdate] = useState<UpdateInfo | null>(null);
  const [phase, setPhase] = useState<UpdatePhase>("idle");
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // AbortController for the in-flight background download so the user
  // can cancel mid-stream on a slow network. The `dismissed` flag is
  // what makes cancellation defer until the *next* 15-min poll instead
  // of restarting instantly: on cancel we set it true, and the poll's
  // success branch clears it. Without this, the auto-download effect
  // would re-fire the moment we transition phase back to "idle". The
  // user can still defer indefinitely, but only by clicking the cross
  // again every 15 minutes — which is the deliberate lever that keeps
  // mandatory updates mandatory.
  const abortRef = useRef<AbortController | null>(null);
  const [dismissed, setDismissed] = useState(false);
  // Version whose bytes are currently staged on disk (set when a download
  // starts). When a later poll surfaces a newer release, we compare against
  // this to decide whether the staged copy has gone stale.
  const stagedVersionRef = useRef<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Check for updates on mount + every 15 minutes while the app stays
  // open. We deliberately avoid blocking the user mid-session: a fresh
  // hit from the poll just kicks off a silent background download (see
  // the auto-download effect below). When the download finishes the
  // pill flips from absent → "Restart to update" — that's the only
  // signal the user sees until they choose to restart.
  useEffect(() => {
    const runCheck = () => {
      apiFetch<UpdateInfo>("/updates/check")
        .then((data) => {
          // Only overwrite if the latest poll actually surfaces an
          // update. Once we've stored an UpdateInfo object the
          // download/apply state machine owns the lifecycle, so we
          // don't want a "no update" response (e.g. transient network
          // hiccup misread as no-update) to clear an in-flight install.
          if (data?.update_available) {
            setUpdate(data);
            // Each successful poll re-arms the auto-download. If the
            // user cancelled the previous attempt, this is exactly the
            // tick we want to retry on. (See the dismissed-flag note
            // on the abortRef declaration above for the full story.)
            setDismissed(false);
          }
        })
        .catch(() => {
          // Network blip — leave any in-flight update state intact.
        });
    };
    runCheck();
    const interval = setInterval(runCheck, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Poll download progress
  useEffect(() => {
    if (phase !== "downloading") return;
    const interval = setInterval(async () => {
      try {
        const status = await apiFetch<{
          downloading: boolean;
          progress: number;
          total: number;
          ready: boolean;
          error: string | null;
        }>("/updates/progress");

        setProgress(status.progress);
        setTotal(status.total);

        if (status.ready) {
          setPhase("ready");
          clearInterval(interval);
        } else if (status.error) {
          setPhase("error");
          setError(status.error);
          clearInterval(interval);
        }
      } catch {
        // Polling failure — keep trying
      }
    }, 500);
    return () => clearInterval(interval);
  }, [phase]);

  const startDownload = useCallback(async () => {
    if (!update?.download_url) return;
    // Belt-and-suspenders against re-entry: phase-gating in the
    // auto-download effect already prevents 15-min ticks from racing,
    // but if startDownload is ever called from two paths (auto + a
    // manual button) the duplicate-call would still POST a second
    // /updates/download. The backend then returns "Download already
    // in progress" — surfacing that as an "error" pill would be
    // misleading because the original download is still healthy. So
    // bail early if we're already past idle.
    if (phase !== "idle") return;
    // Remember what we're staging so the re-arm effect can spot a newer
    // release shipping while this build sits on the "Restart" pill.
    stagedVersionRef.current = update.latest_version ?? null;
    setPhase("downloading");
    setProgress(0);
    setTotal(0);
    setError(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await apiFetch<{ ok: boolean; error?: string }>("/updates/download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          download_url: update.download_url,
          sha256: update.sha256 || "",
          version: update.latest_version || "",
        }),
        signal: controller.signal,
      });
      if (!res.ok) {
        setPhase("error");
        setError(res.error || "Download failed");
      }
      // If ok, the progress poller will pick up "ready"
    } catch (e) {
      // AbortError is the expected outcome of cancelDownload(); the
      // cancel handler has already moved us back to "idle", so we
      // must not clobber phase by transitioning to "error" here.
      if ((e as Error)?.name === "AbortError") return;
      setPhase("error");
      setError(String(e));
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [update, phase]);

  const cancelDownload = useCallback(() => {
    // Aborts the in-flight POST /updates/download. The fetch reject
    // path in startDownload swallows AbortError so we can safely set
    // phase here. `dismissed` blocks the auto-download effect from
    // immediately restarting; the next 15-min poll's success branch
    // clears it, which is what bounds the deferral at 15 minutes.
    abortRef.current?.abort();
    abortRef.current = null;
    setPhase("idle");
    setProgress(0);
    setTotal(0);
    setError(null);
    setDismissed(true);
  }, []);

  // Discard a staged build that a newer release has superseded. The
  // 15-min poll keeps `update` fresh, but the auto-download effect is
  // gated on phase === "idle", so once a build finishes downloading
  // (phase "ready") it would sit there until applied — and the user
  // would restart into a now-stale version. Bouncing back to "idle"
  // re-arms the auto-download for the newer build, with a visible
  // progress bar. (The backend `/apply` re-check is the belt-and-braces
  // backstop for the race between this poll and the user's click.)
  useEffect(() => {
    if (
      phase === "ready" &&
      isNewerVersion(update?.latest_version, stagedVersionRef.current)
    ) {
      setPhase("idle");
      setProgress(0);
      setTotal(0);
      setError(null);
    }
  }, [update, phase]);

  // Auto-start the download in the background as soon as we know an
  // update is available and the platform can apply it in place.
  // Mid-session updates must not interrupt — the user only sees a
  // pill flip to "Restart to update" once the bytes are already on
  // disk. The `phase === "idle"` gate inside startDownload + the
  // dependency on `phase` here is what guarantees the 15-min poll
  // can't kick off a second download: subsequent ticks enter this
  // effect with phase = "downloading" / "ready" / "applying" /
  // "error" and bail. After a manual retry from the error state we
  // reset to "idle" and re-run intentionally.
  useEffect(() => {
    if (
      update?.update_available &&
      update.can_auto_update &&
      phase === "idle" &&
      !dismissed
    ) {
      startDownload();
    }
  }, [update, phase, startDownload, dismissed]);

  const applyUpdate = useCallback(async () => {
    setPhase("applying");
    try {
      // On Linux/macOS the connection drops before this resolves
      // (os.execv replaces the process). On Windows the response returns
      // first, then os._exit fires ~500ms later. Either way, only treat
      // an explicit `ok: false` as a failure — never infer from a 200.
      const res = await apiFetch<{ ok?: boolean; error?: string }>(
        "/updates/apply",
        { method: "POST" },
      );
      if (res && res.ok === false) {
        setError(res.error || "Restart failed — please relaunch manually.");
        setPhase("error");
      }
    } catch {
      // Connection dropped — process is restarting, expected.
    }
  }, []);

  const progressPercent = total > 0 ? Math.round((progress / total) * 100) : 0;

  if (!user) return null;

  // Render the update badge/bar based on phase
  const renderUpdateUI = () => {
    if (!update?.update_available) return null;

    switch (phase) {
      case "idle":
        // can_auto_update path is silent: the auto-download effect
        // is about to flip phase → "downloading" without any user
        // interaction, and the user shouldn't see a transient pill
        // mid-session. Only render the external-link fallback when
        // the platform can't apply updates in place (dev mode,
        // unsupported OS) — that path requires a manual click.
        if (update.can_auto_update) return null;
        return (
          <a
            href={update.download_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-2.5 py-1 bg-oxy-50 text-oxy text-xs font-medium rounded-full hover:bg-oxy/10 transition-colors ring-1 ring-oxy/20"
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            v{update.latest_version} available
          </a>
        );

      case "downloading":
        // Compact in-place pill so the user knows a download is
        // happening and can cancel on a slow network. Cancellation
        // isn't sticky — the next 15-min poll re-arms auto-download —
        // which is intentional; updates are mandatory, the cross
        // just defers them by ~15 minutes at a time.
        return (
          <div className="flex items-center gap-2 pl-3 pr-1 py-1 bg-space-20 rounded-full text-xs text-space-50">
            <div className="w-16 h-1.5 bg-space-30 rounded-full overflow-hidden">
              <div
                className="h-full bg-jove rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="tabular-nums min-w-[3rem] text-right">
              {total > 0 ? `${progressPercent}%` : "..."}
            </span>
            <button
              onClick={cancelDownload}
              className="p-0.5 rounded-full hover:bg-space-30 transition-colors"
              title="Cancel update download"
              aria-label="Cancel update download"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        );

      case "ready":
        return (
          <button
            onClick={applyUpdate}
            className="flex items-center gap-1.5 px-2.5 py-1 bg-elf-50 text-elf text-xs font-medium rounded-full hover:bg-elf/10 transition-colors ring-1 ring-elf/20 animate-pulse"
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Restart to update
          </button>
        );

      case "applying":
        return (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-space-20 text-space-50 text-xs rounded-full">
            <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Restarting...
          </div>
        );

      case "error":
        return (
          <button
            onClick={() => { setPhase("idle"); setError(null); }}
            className="flex items-center gap-1.5 px-2.5 py-1 bg-hydra-50 text-hydra text-xs font-medium rounded-full ring-1 ring-hydra/20"
            title={error || "Update failed"}
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            Update failed — retry
          </button>
        );
    }
  };

  return (
    <header className="h-14 flex items-center justify-between px-4 border-b border-space-30 bg-surface shrink-0">
      <div className="flex items-center gap-3 min-w-0">
        {/* Hamburger — opens the off-canvas sidebar drawer. Mobile only; the
            rail is always visible from `md` up. */}
        <button
          onClick={toggleMobileNav}
          className="md:hidden -ml-1 p-1.5 text-space-50 hover:text-space hover:bg-space-20 rounded-lg transition-colors shrink-0"
          title="Menu"
          aria-label="Open menu"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
          </svg>
        </button>

        <div className="flex items-center gap-2.5 min-w-0">
          <img src="/cowork-logo.svg" alt="Nbyula OmniWerk" className="w-8 h-8 rounded-lg shrink-0" />
          {/* Wordmark hidden on the smallest screens so the header fits the
              hamburger + right-side controls; the logo carries the brand there. */}
          <span className="font-semibold text-space text-sm tracking-tight truncate hidden sm:inline">Nbyula OmniWerk</span>
        </div>

        {renderUpdateUI()}
      </div>

      {/* Claude usage + user menu */}
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        {/* Mobile-only right-panel trigger — shown on a chat that has panel
            content (Plan / Shortlist / Files). Opens the full-screen overlay;
            on desktop the edge strip handles this instead. */}
        {activeConversationId && tabs.length > 0 && (
          <button
            onClick={() => openRightPanel()}
            className="md:hidden relative p-1.5 text-space-50 hover:text-space hover:bg-space-20 rounded-lg transition-colors"
            title="Open panel"
            aria-label="Open side panel"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <rect x="3" y="4.5" width="18" height="15" rx="2" />
              <path strokeLinecap="round" d="M14.5 4.5v15" />
            </svg>
            <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-jove" />
          </button>
        )}

        <UsageBadge />

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-space-20 transition-colors"
          >
          {user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.name}
              className="w-7 h-7 rounded-full ring-2 ring-space-30"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-jove-50 to-jove-80 flex items-center justify-center text-xs font-semibold text-white">
              {user.name.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="text-sm text-space-50 hidden sm:block">{user.name.split(" ")[0]}</span>
          <svg className={`h-3 w-3 text-space-40 transition-transform duration-200 hidden sm:block ${menuOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {menuOpen && (
          <div className="absolute right-0 mt-1.5 w-52 bg-surface rounded-xl shadow-card border border-space-30 py-1.5 z-50 animate-[scaleIn_150ms_ease-out]">
            <div className="px-3.5 py-2.5 border-b border-space-30">
              <p className="text-sm font-semibold text-space truncate">{user.name}</p>
              <p className="text-xs text-space-40 truncate mt-0.5">{user.email}</p>
            </div>
            <div className="py-1">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/settings");
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-space-50 hover:bg-space-20 hover:text-space transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </button>
              {/* Same destination as the mobile install banner's footer link —
                  both read NATIVE_DOWNLOADS_URL so the two surfaces can't drift.
                  Shown in the native shell too: the page carries every platform,
                  so "I'm on Linux, I need the Mac build" is a real errand. */}
              <a
                href={NATIVE_DOWNLOADS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-space-50 hover:bg-space-20 hover:text-space transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Get the desktop app
              </a>
            </div>
            {/* Sign out sits in its own group — a destructive action shouldn't
                be one slip away from a benign one. */}
            <div className="py-1 border-t border-space-30">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-hydra hover:bg-hydra-50 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                </svg>
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </header>
  );
}
